// apps/nos/client/src/lib/instrumentsDto.ts
//
// Mappers between the server's UserExpConfigDTO and the UI's
// InstrumentProfile shape (rooted at utils/types/instrument.ts). The
// server's `methodology` jsonb expands into nested column / inlet / oven /
// ms blocks the drawer expects. ionisation_energy_level is a top-level
// scalar on the DTO but maps to ms.energy_eV in the UI.

import type {
  ConfigCreateInput,
  ConfigDTO,
  ConfigUpdateInput,
} from "@/services/instruments_api";
import type {
  AlkaneLadder,
  CarrierGas,
  ChromatogramFile,
  InjectionMode,
  InstrumentProfile,
  Ionization,
  OvenStep,
} from "@/utils/types/instrument";

type MethColumn = Partial<{ length_m: number; id_mm: number; film_um: number }>;
type MethInlet = Partial<{
  carrier: CarrierGas;
  flow_ml_min: number;
  temp_C: number;
  mode: InjectionMode;
  vol_uL: number;
}>;
type MethMs = Partial<{
  sourceTemp_C: number;
  scanLo: number;
  scanHi: number;
  mode: string;
  ionization: Ionization;
}>;
type MethBlob = Partial<{
  column: MethColumn;
  inlet: MethInlet;
  oven: OvenStep[];
  ms: MethMs;
  notes: string;
}>;

export type DtoToProfileExtras = {
  /** Resolved tag display texts for this config's tag_ids. Defaults to []. */
  tags?: string[];
  /** Attached chromatograms for the expand view. Defaults to []. */
  chromatograms?: ChromatogramFile[];
  /** Attached alkane ladders for the expand view. Defaults to []. */
  alkaneLadder?: AlkaneLadder[];
};

export function dtoToProfile(
  dto: ConfigDTO,
  extras: DtoToProfileExtras = {},
): InstrumentProfile {
  const meth = (dto.methodology ?? {}) as MethBlob;
  const col = meth.column ?? {};
  const inlet = meth.inlet ?? {};
  const ms = meth.ms ?? {};
  return {
    id: dto.id,
    alias: dto.name,
    instrumentId: dto.type_instrument_id,
    // Every Advanced methodology field is null when the user didn't
    // enter it. Picker enums (carrier, inlet mode, ionization, scan mode)
    // are no longer pre-populated — the drawer renders "Choose…" until
    // the user picks.
    column: {
      id: dto.type_column_id,
      length_m: col.length_m ?? null,
      id_mm: col.id_mm ?? null,
      film_um: col.film_um ?? null,
    },
    inlet: {
      carrier: inlet.carrier ?? null,
      flow_ml_min: inlet.flow_ml_min ?? null,
      temp_C: inlet.temp_C ?? null,
      mode: inlet.mode ?? null,
      vol_uL: inlet.vol_uL ?? null,
    },
    oven: meth.oven ?? [],
    ms: {
      ionization: ms.ionization ?? null,
      sourceTemp_C: ms.sourceTemp_C ?? null,
      energy_eV: dto.ionisation_energy_level,
      scanLo: ms.scanLo ?? null,
      scanHi: ms.scanHi ?? null,
      mode: ms.mode ?? null,
    },
    methodology: { notes: meth.notes ?? "" },
    alkaneLadder: extras.alkaneLadder ?? [],
    ladderCount: dto.ladder_count,
    chromatogramCount: dto.chromatogram_count,
    tags: extras.tags ?? [],
    notes: dto.notes ?? "",
    createdAt: dto.created_at,
    lastUsedAt: null, // not surfaced on the DTO yet
    chromatograms: extras.chromatograms ?? [],
  };
}

/**
 * Inverse of dtoToProfile — flatten the UI's InstrumentProfile back into
 * the wire shape the server expects for POST /me/instruments/configs.
 *
 * Required fields (per spec §3.1) must be present: alias, instrumentId,
 * column.id, ionisation_energy_level. The caller is expected to gate
 * Submit on the same `canSave` check the drawer uses.
 *
 * Optional Advanced methodology fields are **omitted entirely** from
 * the methodology jsonb when null — placeholder values must never be
 * submitted as defaults. Per-step oven entries keep null fields as null
 * so the server / R&D side can decide how to interpret a half-filled
 * step the user explicitly added.
 */
export function profileToCreateInput(p: InstrumentProfile): ConfigCreateInput {
  if (p.instrumentId == null) {
    throw new Error("profileToCreateInput: instrumentId is required");
  }
  if (p.column.id == null) {
    throw new Error("profileToCreateInput: column.id is required");
  }
  if (p.ms.energy_eV == null) {
    throw new Error("profileToCreateInput: ms.energy_eV is required");
  }

  // Build the methodology jsonb without any default-valued keys. Picker
  // enums are always present (they have pre-selected pills) and are kept.
  const methodology: Record<string, unknown> = {};

  const column: Record<string, unknown> = {};
  if (p.column.length_m != null) column.length_m = p.column.length_m;
  if (p.column.id_mm != null) column.id_mm = p.column.id_mm;
  if (p.column.film_um != null) column.film_um = p.column.film_um;
  if (Object.keys(column).length > 0) methodology.column = column;

  const inlet: Record<string, unknown> = {};
  if (p.inlet.carrier != null) inlet.carrier = p.inlet.carrier;
  if (p.inlet.mode != null) inlet.mode = p.inlet.mode;
  if (p.inlet.flow_ml_min != null) inlet.flow_ml_min = p.inlet.flow_ml_min;
  if (p.inlet.temp_C != null) inlet.temp_C = p.inlet.temp_C;
  if (p.inlet.vol_uL != null) inlet.vol_uL = p.inlet.vol_uL;
  if (Object.keys(inlet).length > 0) methodology.inlet = inlet;

  if (p.oven.length > 0) methodology.oven = p.oven;

  const ms: Record<string, unknown> = {};
  if (p.ms.ionization != null) ms.ionization = p.ms.ionization;
  if (p.ms.mode != null) ms.mode = p.ms.mode;
  if (p.ms.sourceTemp_C != null) ms.sourceTemp_C = p.ms.sourceTemp_C;
  if (p.ms.scanLo != null) ms.scanLo = p.ms.scanLo;
  if (p.ms.scanHi != null) ms.scanHi = p.ms.scanHi;
  if (Object.keys(ms).length > 0) methodology.ms = ms;

  if (p.methodology?.notes && p.methodology.notes.trim().length > 0) {
    methodology.notes = p.methodology.notes;
  }

  return {
    name: p.alias.trim(),
    notes: p.notes ?? null,
    type_instrument_id: p.instrumentId,
    type_column_id: p.column.id,
    ionisation_energy_level: p.ms.energy_eV,
    methodology,
  };
}

/**
 * Build the methodology-keys PATCH body for the Save & overwrite flow.
 *
 * The server's update endpoint switches on whether ANY methodology key is
 * present in the body (per controllers/me/instruments/configs/update.ts):
 * if so, it runs the full §6 R&D round-trip; if only autosave keys
 * (name / notes) are present it does a cheap UPDATE. To trigger the R&D
 * path we always include the complete methodology surface — server-side
 * the jsonb is replaced wholesale.
 *
 * Returns the same shape as profileToCreateInput minus `tag_ids` (tags
 * are managed via dedicated attach / detach endpoints, not via PATCH).
 */
export function profileToUpdateInput(p: InstrumentProfile): ConfigUpdateInput {
  const { tag_ids: _tagIds, ...rest } = profileToCreateInput(p);
  return rest;
}
